import { useQuery, useMutation } from '@tanstack/react-query'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import toast from 'react-hot-toast'
import { Trash2 } from 'lucide-react'
import { api, ApiError, type SpreadKind } from '../../api/client'
import { Button } from '../../components/ui/Button'
import { BackButton } from '../../components/ui/BackButton'
import { ShareButton } from '../../components/ui/ShareButton'
import { SectionedMarkdown } from '../../components/ui/SectionedMarkdown'
import { TarotFlipCard } from '../../components/tarot/TarotFlipCard'

const KIND_LABEL_RU: Record<SpreadKind, string> = {
  '3card': 'Расклад на 3 карты',
  '5card_relationship': 'Расклад на отношения',
  celtic_cross: 'Кельтский крест',
}

const POSITION_KEYS: Record<SpreadKind, string[]> = {
  '3card': ['tarot.pos_3_past', 'tarot.pos_3_present', 'tarot.pos_3_future'],
  '5card_relationship': [
    'tarot.pos_5_you',
    'tarot.pos_5_partner',
    'tarot.pos_5_bond',
    'tarot.pos_5_obstacle',
    'tarot.pos_5_direction',
  ],
  celtic_cross: [
    'tarot.pos_celtic_heart',
    'tarot.pos_celtic_cross',
    'tarot.pos_celtic_foundation',
    'tarot.pos_celtic_past',
    'tarot.pos_celtic_possible_future',
    'tarot.pos_celtic_near_future',
    'tarot.pos_celtic_you_now',
    'tarot.pos_celtic_environment',
    'tarot.pos_celtic_hopes_fears',
    'tarot.pos_celtic_outcome',
  ],
}

// Same compact card sizes as the spread page's "done" stage so a historical
// reading looks visually identical to a freshly-flipped one.
const COMPACT_CARD_WIDTH: Record<SpreadKind, string> = {
  '3card': 'w-20',
  '5card_relationship': 'w-[68px]',
  celtic_cross: 'w-14',
}

interface ReadingCardProps {
  cards: Array<{ id: string; reversed: boolean; position: number }>
  kind: SpreadKind
  positionLabels: string[]
}

function CardsStrip({ cards, kind, positionLabels }: ReadingCardProps) {
  const cardWidth = COMPACT_CARD_WIDTH[kind]
  return (
    <div className="flex flex-row flex-wrap justify-center gap-2 sm:gap-3">
      {cards.map((c, i) => {
        const label = positionLabels[i] ?? `#${i + 1}`
        return (
          <div key={i} className="flex flex-col items-center gap-1">
            <TarotFlipCard
              cardId={c.id}
              reversed={c.reversed}
              revealed
              className={cardWidth}
              positionLabel={label}
            />
            <span className="text-center text-[11px] leading-tight text-sage-600">{label}</span>
          </div>
        )
      })}
    </div>
  )
}

/**
 * Owner-view of a previously created reading. Renders the same cards + reading
 * layout as the spread page's "done" stage so a history entry looks identical
 * to a freshly-flipped one, with Share + Delete actions appended below.
 */
export function TarotReadingRoute() {
  const { id } = useParams<{ id: string }>()
  const { t } = useTranslation()
  const navigate = useNavigate()

  const readingQ = useQuery({
    queryKey: ['tarot', 'reading', id],
    queryFn: () => api.tarotGet(id!),
    enabled: !!id,
  })

  const shareMut = useMutation({
    mutationFn: () => api.tarotShare(id!),
    onError: (err) => {
      const msg = err instanceof ApiError ? err.body.error.message : (err as Error).message
      toast.error(msg)
    },
  })

  const deleteMut = useMutation({
    mutationFn: () => api.tarotDelete(id!),
    onSuccess: () => {
      toast.success(t('tarot_history.deleted'))
      navigate('/tarot/history', { replace: true })
    },
    onError: (err) => {
      const msg = err instanceof ApiError ? err.body.error.message : (err as Error).message
      toast.error(msg)
    },
  })

  if (readingQ.isLoading) {
    return (
      <div className="flex flex-col gap-5 pb-6">
        <BackButton to="/tarot/history" />
        <div className="h-40 animate-pulse rounded-2xl bg-sage-100" />
      </div>
    )
  }

  if (!readingQ.data) {
    return (
      <div className="flex flex-col gap-5 pb-6">
        <BackButton to="/tarot/history" />
        <h1 className="font-serif text-2xl text-sage-800">{t('tarot_history.not_found')}</h1>
        <p className="text-sage-700">{t('tarot_history.not_found_hint')}</p>
      </div>
    )
  }

  const r = readingQ.data
  const dt = new Date(r.createdAt)
  const shareUrl = shareMut.data?.shareUrl
  const positions = POSITION_KEYS[r.kind].map((k) => t(k))

  return (
    <div className="flex flex-col gap-5 pb-6">
      <header className="flex items-center justify-between">
        <BackButton to="/tarot/history" />
      </header>

      <div className="flex flex-col gap-1 text-center">
        <h1 className="font-serif text-xl text-sage-800">{KIND_LABEL_RU[r.kind]}</h1>
        <p className="text-xs text-sage-500">
          {dt.toLocaleString('ru-RU', { dateStyle: 'long', timeStyle: 'short' })}
        </p>
      </div>

      <CardsStrip cards={r.cards} kind={r.kind} positionLabels={positions} />

      <section>
        <h3 className="mb-3 font-serif text-lg text-sage-800">{t('tarot.reading')}</h3>
        <SectionedMarkdown>{r.interpretation}</SectionedMarkdown>
      </section>

      <div className="flex flex-col gap-2">
        {shareUrl ? (
          <ShareButton
            url={shareUrl}
            text={t('tarot_history.share_text')}
            label={t('tarot_history.share_again')}
            size="md"
          />
        ) : (
          <Button
            variant="primary"
            size="md"
            loading={shareMut.isPending}
            onClick={() => shareMut.mutate()}
          >
            {t('tarot_history.share')}
          </Button>
        )}

        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            if (window.confirm(t('tarot_history.confirm_delete'))) {
              deleteMut.mutate()
            }
          }}
          loading={deleteMut.isPending}
          leftIcon={<Trash2 className="h-4 w-4" strokeWidth={1.8} aria-hidden />}
        >
          {t('tarot_history.delete')}
        </Button>
      </div>
    </div>
  )
}

/**
 * Public read-only view of a shared reading. No auth check on the backend —
 * the route token IS the auth. Same cards layout as the owner view but with
 * no Share/Delete actions and a Back button that returns to the user's own
 * Tarot section (most users opening a shared link are guests; the back
 * navigation is a graceful escape hatch).
 */
export function TarotSharedRoute() {
  const { token } = useParams<{ token: string }>()
  const { t } = useTranslation()

  const q = useQuery({
    queryKey: ['tarot', 'shared', token],
    queryFn: () => api.tarotShared(token!),
    enabled: !!token,
  })

  if (q.isLoading) {
    return (
      <div className="flex flex-col gap-5 pb-6">
        <BackButton to="/tarot" />
        <div className="h-40 animate-pulse rounded-2xl bg-sage-100" />
      </div>
    )
  }

  if (!q.data) {
    return (
      <div className="flex flex-col gap-5 pb-6">
        <BackButton to="/tarot" />
        <h1 className="font-serif text-2xl text-sage-800">{t('tarot_history.shared_gone')}</h1>
        <p className="text-sage-700">{t('tarot_history.shared_gone_hint')}</p>
      </div>
    )
  }

  const r = q.data
  const dt = new Date(r.createdAt)
  const positions = POSITION_KEYS[r.kind].map((k) => t(k))

  return (
    <div className="flex flex-col gap-5 pb-6">
      <header className="flex items-center justify-between">
        <BackButton to="/tarot" />
      </header>

      <div className="flex flex-col gap-1 text-center">
        <h1 className="font-serif text-xl text-sage-800">{KIND_LABEL_RU[r.kind]}</h1>
        <p className="text-xs text-sage-500">
          {t('tarot_history.shared_by_someone')} ·{' '}
          {dt.toLocaleString('ru-RU', { dateStyle: 'long', timeStyle: 'short' })}
        </p>
      </div>

      {r.question && (
        <p className="rounded-2xl bg-amber-50 px-3 py-2 text-sm italic text-amber-900 ring-1 ring-amber-200">
          «{r.question}»
        </p>
      )}

      <CardsStrip cards={r.cards} kind={r.kind} positionLabels={positions} />

      <section>
        <h3 className="mb-3 font-serif text-lg text-sage-800">{t('tarot.reading')}</h3>
        <SectionedMarkdown>{r.interpretation}</SectionedMarkdown>
      </section>
    </div>
  )
}
